import React, { useState, useEffect, useRef } from 'react';
import { professionalsAPI } from '../services/api';
import useIsMobile from '../hooks/useIsMobile';
import toast from 'react-hot-toast';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
// [FIX 01/set/2026] Agrupador de pins — resolve a sobreposição de nomes de oficina no mapa.
// Não era coordenada errada (quase todo profissional já tem latitude/longitude reais no
// banco); era simplesmente ter várias oficinas de verdade perto umas das outras, cada uma com
// seu balãozinho de nome, todos disputando o mesmo pedaço de tela. O MarkerClusterGroup junta
// pins próximos num círculo com o número (ex: "23"), que se abre em pins individuais ao dar
// zoom — nenhuma oficina fica escondida, só organizada. Pacote gratuito (react-leaflet-cluster,
// wrapper do Leaflet.markercluster), sem custo nenhum.
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/* ── Custom Leaflet Icons ────────────────── */
// [FIX 01/set/2026] Os pinos de oficina eram uma caixa BRANCA com um textinho escuro e só
// uma tarja fina colorida na borda esquerda — em cima do mapa (que também é claro, bege/
// verde-claro), isso quase some, sobra só aquela tarjinha colorida torta parecendo um "(".
// Virou o oposto: fundo SÓLIDO na cor da marca, texto branco, contorno branco de verdade em
// volta (não só um risquinho) — igual ao badge do cluster, que já lia bem. Ponteiro embaixo
// também virou a mesma cor sólida, pra ler como um balão único, não dois pedaços soltos.
//
// [FIX 01/set/2026 #2] Causa raiz do texto sem fundo: o marcador usa iconSize:[0,0] de
// propósito (é o truque padrão pra deixar o conteúdo "vazar" pra fora de um elemento sem
// tamanho, já que o Leaflet posiciona esse elemento 0×0 exatamente em cima do ponto do
// mapa). O problema é que "display:flex" continua sendo um elemento em BLOCO por fora —
// e um bloco sem largura definida herda 100% da largura do pai, que aqui é 0px. Ou seja: a
// caixinha colorida "encolhia" pra ~0px de largura, sobrava só a borda/padding (o "pin"
// pequeno que aparecia), e o texto — que não quebra linha — vazava pra fora dela. Como o
// fundo colorido só pinta a área da própria caixa (não acompanha o que vaza), o texto ficava
// boiando direto em cima do mapa, sem fundo nenhum atrás — por isso ilegível quando é
// branco. "display:inline-flex" faz a caixa se ajustar ao tamanho do próprio conteúdo (texto
// + padding), em vez de herdar a largura do pai — resolve na raiz, não só o contraste.
const createIcon = (color, label) => L.divIcon({
  className: '',
  html: `<div style="
    background:${color};color:#fff;padding:6px 12px;border-radius:8px;
    font-size:11.5px;font-weight:700;font-family:'DM Sans',sans-serif;
    white-space:nowrap;box-shadow:0 2px 10px rgba(0,0,0,.35);
    display:inline-flex;align-items:center;gap:4px;width:max-content;
    border:2px solid #fff;
    transform:translate(-50%,-100%);position:relative;
  ">
    ${label}
    <div style="position:absolute;bottom:-8px;left:50%;transform:translateX(-50%);
      width:0;height:0;border-left:7px solid transparent;border-right:7px solid transparent;
      border-top:8px solid ${color};"></div>
  </div>`,
  iconSize: [0, 0],
  iconAnchor: [0, 0],
});

const proIcon = (name) => createIcon('#14562e', name);

// [FIX 01/set/2026] "Você" usava o mesmo balãozinho de texto dos profissionais — ficava
// pequeno, ilegível, e ainda disputava espaço/lugar com o círculo do cluster bem em cima
// dele. Trocado pelo padrão universal de "sua localização" em mapas: um pontinho sólido com
// contorno branco e um halo translúcido ao redor, na cor laranja da legenda — dá pra achar
// de relance, sem precisar ler texto nenhum.
const youIcon = L.divIcon({
  className: '',
  html: `<div style="position:relative;width:34px;height:34px;">
    <div style="position:absolute;inset:0;border-radius:50%;background:rgba(233,113,23,.25);"></div>
    <div style="position:absolute;top:9px;left:9px;width:16px;height:16px;border-radius:50%;
      background:#e97117;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);"></div>
  </div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

// Ícone do agrupador de pins — bolinha verde com a contagem, no mesmo estilo visual do resto
// do mapa (cor da marca, fonte Bricolage Grotesque), em vez do ícone padrão amarelo da lib.
const createClusterIcon = (cluster) => L.divIcon({
  html: `<div style="
    background:#14562e;color:#fff;width:38px;height:38px;border-radius:50%;
    display:flex;align-items:center;justify-content:center;
    font-family:'Bricolage Grotesque',sans-serif;font-weight:800;font-size:13px;
    box-shadow:0 2px 8px rgba(0,0,0,.25);border:2px solid #fff;
  ">${cluster.getChildCount()}</div>`,
  className: '',
  iconSize: L.point(38, 38, true),
});

/* ── Fit bounds component ───────────────── */
function FitBounds({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [markers, map]);
  return null;
}

/* ── Styles ─────────────────────────────── */
const P = {
  eyebrow: { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.8px', color: '#e97117', marginBottom: '6px' },
  h1: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, textTransform: 'uppercase', lineHeight: 0.95, letterSpacing: '-0.02em', margin: 0, color: '#14562e', wordBreak: 'break-word', overflowWrap: 'break-word' },
  subtitle: { color: '#6a7268', margin: '6px 0 0', fontSize: '15px' },
  grid: { display: 'grid', gap: '24px', marginTop: '24px' },
  searchBox: {
    display: 'flex', alignItems: 'center', gap: '10px',
    background: '#fff', padding: '10px 16px', borderRadius: '12px',
    border: '1px solid #e5ddc9', maxWidth: '320px',
  },
  searchInput: {
    border: 'none', outline: 'none', background: 'transparent', flex: 1,
    fontSize: '14px', fontFamily: "'DM Sans', sans-serif", color: '#183d24',
  },
  list: { display: 'flex', flexDirection: 'column', gap: '0', border: '1px solid #e5ddc9', borderRadius: '16px', background: '#fff', overflow: 'hidden', maxHeight: '65vh', overflowY: 'auto' },
  card: { padding: '20px 24px', borderBottom: '1px solid #efe8d6', cursor: 'pointer', transition: 'background 0.15s' },
  cardActive: { background: '#e6f0e9' },
  name: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: '16px', color: '#14562e', margin: 0, display: 'inline' },
  nicoBadge: { background: '#14562e', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: '999px', marginLeft: '8px', letterSpacing: '0.5px', verticalAlign: 'middle' },
  stars: { color: '#e97117', fontSize: '12px', letterSpacing: '1px', fontWeight: 700, whiteSpace: 'nowrap' },
  rating: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, fontSize: '16px', color: '#14562e', marginLeft: '4px' },
  info: { fontSize: '13px', color: '#6a7268', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' },
  tags: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' },
  tag: { padding: '3px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', background: '#f4ede0', color: '#6a7268', border: '1px solid #e5ddc9' },
  mapWrap: { borderRadius: '16px', overflow: 'hidden', border: '1px solid #e5ddc9', minHeight: '500px', position: 'relative' },
  legend: {
    position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)',
    background: '#fff', padding: '8px 16px', borderRadius: '999px', zIndex: 1000,
    display: 'flex', gap: '16px', fontSize: '12px', fontWeight: 600, border: '1px solid #e5ddc9',
    boxShadow: '0 2px 8px rgba(0,0,0,.1)',
  },
  legendDot: { width: '10px', height: '10px', borderRadius: '50%', display: 'inline-block', marginRight: '6px' },
  directionsBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 12px',
    borderRadius: '8px', background: '#14562e', color: '#fff', fontSize: '12px',
    fontWeight: 600, textDecoration: 'none', marginTop: '8px',
  },
};

function starsStr(rating) {
  const full = Math.floor(rating || 0);
  return '★'.repeat(full) + '☆'.repeat(5 - full);
}

function getServiceTags(p) {
  try {
    const types = typeof p.service_types === 'string' ? JSON.parse(p.service_types) : p.service_types;
    return Array.isArray(types) ? types : [];
  } catch { return []; }
}

/* ── Default coordinates (Rio de Janeiro) ── */
const DEFAULT_LAT = -22.9068;
const DEFAULT_LNG = -43.1729;

// [FIX 01/set/2026] A CARTO passou a exigir uma API key até pro tier gratuito de tiles —
// sem ela, o mapa mostra um aviso "API KEY REQUIRED" no lugar do tile de verdade (aconteceu
// em produção). A key em si não é um segredo de servidor (é só um identificador de uso, do
// jeito que a própria CARTO documenta — nenhuma conta é necessária pra pedir uma), mas ainda
// assim fica em variável de ambiente pra seguir o mesmo padrão do resto do projeto
// (REACT_APP_API_URL etc.) e pra dar pra trocar sem precisar editar/rebuildar o código.
const CARTO_API_KEY = process.env.REACT_APP_CARTO_KEY || '';

export default function ProfessionalsPage() {
  const m = useIsMobile();
  const [professionals, setProfessionals] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [userPos, setUserPos] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const mapRef = useRef(null);

  // Get user geolocation
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setUserPos({ lat: DEFAULT_LAT, lng: DEFAULT_LNG }),
        { timeout: 5000 }
      );
    } else {
      setUserPos({ lat: DEFAULT_LAT, lng: DEFAULT_LNG });
    }
  }, []);

  useEffect(() => {
    professionalsAPI.search({})
      .then(r => setProfessionals(r.professionals || []))
      .catch(() => toast.error('Erro ao buscar profissionais'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = professionals.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (p.business_name || '').toLowerCase().includes(q) ||
      (p.specialty || '').toLowerCase().includes(q) ||
      (p.address_city || '').toLowerCase().includes(q) ||
      (p.user_name || '').toLowerCase().includes(q)
    );
  });

  // [FIX 01/set/2026] Antes, todo profissional sem latitude/longitude reais (ex.: cadastro
  // sem CEP nem cidade preenchida) ganhava uma coordenada INVENTADA, espalhada aleatoriamente
  // ao redor da localização do usuário que está com o mapa aberto. Resultado visível em
  // produção: a "Mecânica Paiva" (que não tem CEP/cidade cadastrados) aparecia com um pino
  // colado no "Você" no Rio de Janeiro, mesmo sendo uma oficina de Petrópolis — parecia dado
  // real, mas não era posição nenhuma, só sorteio. Agora só entra no mapa quem tem
  // coordenada de verdade; quem ainda não tem continua aparecendo normalmente na lista ao
  // lado (sem pino), até que o próprio profissional complete o cadastro com CEP ou cidade.
  const prosWithCoords = filtered
    .filter(p => p.latitude && p.longitude)
    .map(p => ({ ...p, lat: p.latitude, lng: p.longitude }));

  const center = userPos || { lat: DEFAULT_LAT, lng: DEFAULT_LNG };

  const handleCardClick = (p) => {
    setSelectedId(p.id);
    // A lista agora inclui profissionais sem coordenada real (que não têm pino no mapa) —
    // só tenta voar até a posição quando ela existe de fato.
    if (mapRef.current && p.latitude && p.longitude) {
      mapRef.current.flyTo([p.latitude, p.longitude], 15, { duration: 0.8 });
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: '12px' }}>
      <div className="spinner" style={{ width: '32px', height: '32px' }} />
      <span style={{ fontSize: '13px', color: '#6a7268' }}>Buscando oficinas...</span>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: m ? 'flex-start' : 'flex-end', gap: '16px', flexWrap: 'wrap', marginBottom: '4px', flexDirection: m ? 'column' : 'row' }}>
        <div>
          <span style={P.eyebrow}>Rede Nico</span>
          <h1 style={{ ...P.h1, fontSize: m ? '24px' : '38px' }}>Profissionais perto de você</h1>
          <p style={P.subtitle}>Oficinas e mecânicos verificados na sua região.</p>
        </div>
        <div style={P.searchBox}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6a7268" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input style={P.searchInput} placeholder="Buscar por serviço, bairro…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div style={{ ...P.grid, gridTemplateColumns: m ? '1fr' : '1fr 1fr' }}>
        {/* List */}
        <div style={P.list}>
          {filtered.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: '40px', opacity: 0.3, marginBottom: '12px' }}>🔍</div>
              <p style={{ color: '#6a7268', fontSize: '14px', margin: 0 }}>
                {professionals.length === 0 ? 'Nenhum profissional cadastrado ainda.' : 'Nenhum resultado para essa busca.'}
              </p>
            </div>
          ) : (
            filtered.map((p, i) => {
              const tags = getServiceTags(p);
              const isSelected = selectedId === p.id;
              return (
                <div
                  key={p.id}
                  style={{ ...P.card, ...(i === filtered.length - 1 ? { borderBottom: 'none' } : {}), ...(isSelected ? P.cardActive : {}) }}
                  onClick={() => handleCardClick(p)}
                  onMouseOver={e => { if (!isSelected) e.currentTarget.style.background = '#f9f5ec'; }}
                  onMouseOut={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <div>
                      <h3 style={P.name}>{p.business_name || p.user_name}</h3>
                      {!!p.is_verified && <span style={P.nicoBadge}>✓ NICO</span>}
                    </div>
                    {p.rating_avg > 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={P.stars}>{starsStr(p.rating_avg)}</span>
                        <span style={P.rating}>{(p.rating_avg || 0).toFixed(1)}</span>
                      </div>
                    ) : null}
                  </div>
                  <div style={P.info}>
                    <span>📍 {p.address_city || 'Localização'}{p.address_state ? `, ${p.address_state}` : ''}</span>
                  </div>
                  {p.whatsapp && <div style={{ ...P.info, marginTop: '2px' }}><span>📞 {p.whatsapp}</span></div>}
                  {tags.length > 0 ? (
                    <div style={P.tags}>
                      {tags.slice(0, 5).map((t, j) => (
                        <span key={j} style={P.tag}>{typeof t === 'string' ? t.toUpperCase() : t}</span>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>

        {/* Map */}
        <div style={P.mapWrap}>
          <MapContainer
            center={[center.lat, center.lng]}
            zoom={13}
            style={{ height: '100%', minHeight: '500px', width: '100%' }}
            ref={mapRef}
            zoomControl={!m}
          >
            <TileLayer
              url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png${CARTO_API_KEY ? `?key=${CARTO_API_KEY}` : ''}`}
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
            />

            {/* User marker */}
            {userPos && (
              <Marker position={[userPos.lat, userPos.lng]} icon={youIcon}>
                <Popup>Sua localização</Popup>
              </Marker>
            )}

            {/* Professional markers — agrupados quando estão perto uns dos outros */}
            <MarkerClusterGroup
              iconCreateFunction={createClusterIcon}
              maxClusterRadius={60}
              spiderfyOnMaxZoom
              showCoverageOnHover={false}
            >
              {prosWithCoords.map(p => {
                const shortName = (p.business_name || p.user_name || '').split(' ').slice(0, 2).join(' ');
                return (
                  <Marker key={p.id} position={[p.lat, p.lng]} icon={proIcon(shortName)}>
                    <Popup>
                      <div style={{ fontFamily: "'DM Sans', sans-serif", minWidth: '180px' }}>
                        <b style={{ fontSize: '14px', color: '#14562e' }}>{p.business_name || p.user_name}</b>
                        {!!p.is_verified && <span style={{ ...P.nicoBadge, fontSize: '9px', marginLeft: '6px' }}>✓ NICO</span>}
                        {p.address_city && <div style={{ fontSize: '12px', color: '#6a7268', marginTop: '4px' }}>📍 {p.address_city}, {p.address_state}</div>}
                        {p.whatsapp && <div style={{ fontSize: '12px', color: '#6a7268', marginTop: '2px' }}>📞 {p.whatsapp}</div>}
                        {p.specialty && <div style={{ fontSize: '12px', color: '#6a7268', marginTop: '2px' }}>🔧 {p.specialty}</div>}
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`}
                          target="_blank" rel="noreferrer"
                          style={P.directionsBtn}
                        >
                          📍 Como chegar
                        </a>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>

            {/* [FIX 01/set/2026] Antes isso rodava sempre, dando fit-bounds em TODOS os
                profissionais carregados (quase 1500, espalhados pelo Brasil — e ao menos 1
                com coordenada claramente errada lá na Europa, "José Cartaxo", que puxou o
                enquadramento pro planeta inteiro). Resultado: a página sempre abria com o
                mapa zoom out ao extremo, em vez de mostrar quem está perto. Agora o zoom
                inicial é sempre a vizinhança do usuário (center/zoom do MapContainer, logo
                abaixo); só reenquadra automaticamente quando há uma busca ativa, pra focar
                nos resultados encontrados — sem busca, quem quiser ver mais longe dá zoom
                out manualmente. */}
            {search && <FitBounds markers={prosWithCoords} />}
          </MapContainer>

          {/* Legend */}
          <div style={P.legend}>
            <span><span style={{ ...P.legendDot, background: '#e97117' }} /> Você</span>
            <span><span style={{ ...P.legendDot, background: '#14562e' }} /> Oficina verificada</span>
          </div>
        </div>
      </div>
    </div>
  );
}
